import { useTranslations } from '@lang';
import moment from 'moment';
import { useFormContext } from 'react-hook-form';
import type { ItineraryGuest, ItineraryStay } from 'voyager';
import { usePackageConfiguration, useWizardNavigation } from '../contexts';
import { ExperienceSlot } from '../FormSteps/ExperiencesStepV2/buildExperienceSlots';
import { useExperienceSlots } from '../FormSteps/ExperiencesStepV2/useExperienceSlots';
import { buildPackageItinerary, PackageItineraryEntry } from '../helpers/buildPackageItinerary';
import { formatDateISO } from '../helpers/dateFormatters';
import { findOptionFromConfigGroups } from '../helpers/findOptionFromConfigGroups';
import { getEffectiveExperienceDate } from '../helpers/getEffectiveExperienceDate';
import {
  getDiningDate,
  getLastDayNumber,
  resolveDiningDayNumber,
} from '../helpers/groupDiningOptionsByDate';
import { DynamicPackagesFormStepNames, PackagesFormValues } from '../types';

export const usePackageItinerary = (): { guests: ItineraryGuest[]; stay?: ItineraryStay } => {
  const { t } = useTranslations();
  const { rate, room, journeyParams, spaGuestAmount, language } = usePackageConfiguration();
  const { allFormSteps } = useWizardNavigation();
  const { watch } = useFormContext<PackagesFormValues>();
  const formGuests = watch('guests') ?? [];
  const restaurants = watch('tables')?.restaurants ?? [];

  const experienceStep = allFormSteps.find(
    ({ step }) => step === DynamicPackagesFormStepNames.EXPERIENCE
  );
  const diningOptions =
    allFormSteps.find(({ step }) => step === DynamicPackagesFormStepNames.TABLES)?.diningOptions ??
    [];
  const { spaDay, packageConfigGroups, slots } = useExperienceSlots(experienceStep);

  const { fromDate, toDate } = journeyParams;
  const roomName = (Array.isArray(rate.rooms) ? rate.rooms : []).find(
    ({ id }) => String(id) === String(room.id)
  )?.name;
  const awaitingChoice = t('package.itinerary.awaitingChoice');
  const diningTitle = t('package.itinerary.dining');

  const slotTitle = (slot: ExperienceSlot, optionId: number | string | undefined) => {
    const optionName =
      optionId === undefined
        ? undefined
        : findOptionFromConfigGroups(packageConfigGroups, Number(optionId))?.name ??
          slot.choiceGroup?.options.find((option) => Number(option.optionId) === Number(optionId))
            ?.name;
    const groupName = packageConfigGroups.find(
      ({ id }) => String(id) === String(slot.optionGroupId)
    )?.name;

    return optionName || groupName || t('package.itinerary.spaExperience');
  };

  const spaEntries = (guestIndex: number): PackageItineraryEntry[] =>
    slots.map((slot) => {
      const booking = formGuests[guestIndex]?.experiences?.[slot.index];
      const { formattedDate } = getEffectiveExperienceDate(
        fromDate,
        booking?.dayNumber,
        slot.dayNumberRange
      );
      const optionId =
        booking?.optionId ?? (slot.kind === 'included' ? slot.option?.optionId : undefined);
      const title = slotTitle(slot, optionId);

      return booking?.startTime
        ? {
            kind: 'booked',
            date: formattedDate,
            time: booking.startTime.slice(0, 5),
            title,
            location: spaDay?.name,
          }
        : { kind: 'pending', date: formattedDate, title, location: awaitingChoice };
    });

  const lastDayNumber = getLastDayNumber(fromDate, toDate);
  const diningEntries: PackageItineraryEntry[] = diningOptions.map((option, index) => {
    const booking = restaurants[index];
    const date = getDiningDate(
      fromDate,
      resolveDiningDayNumber(option, booking?.dayNumber, lastDayNumber)
    );
    const timeSlot = booking?.timeslot?.timeSlot;

    return timeSlot
      ? {
          kind: 'booked',
          date,
          time: moment.parseZone(timeSlot).format('HH:mm'),
          title: diningTitle,
          location: booking?.restaurant?.configName || option.name,
        }
      : { kind: 'pending', date, title: diningTitle, location: awaitingChoice };
  });

  const sharedEntries: PackageItineraryEntry[] = [
    {
      kind: 'checkIn',
      date: formatDateISO(moment(fromDate)),
      title: t('package.itinerary.checkIn'),
      location: roomName,
    },
    ...diningEntries,
    {
      kind: 'checkOut',
      date: formatDateISO(toDate ? moment(toDate) : moment(fromDate).add(1, 'days')),
      title: t('package.itinerary.checkOut'),
      location: roomName,
    },
  ];

  const guestCount = Math.max(formGuests.length, spaGuestAmount, 1);
  const guests = Array.from({ length: guestCount }, (_, index) => {
    const details = formGuests[index]?.guestDetails;
    const name = [details?.firstName, details?.lastName]
      .map((part) => part?.trim())
      .filter(Boolean)
      .join(' ');

    return {
      id: String(index),
      name: name || t('package.itinerary.guest', { number: index + 1 }),
      entries: spaEntries(index),
    };
  });

  return {
    guests: buildPackageItinerary(guests, sharedEntries, (isoDate) =>
      moment(isoDate).locale(language).format('D MMM, ddd')
    ),
    stay: roomName ? { label: t('package.itinerary.stayingAt'), name: roomName } : undefined,
  };
};
